/**
 * Copy for Stripe's own codes.
 *
 * The API passes through the most specific code it was given — the bank's
 * `decline_code` where there is one, else Stripe's `code` — and the client
 * adapter's `translateError` keys into this. Anything not listed falls back
 * to the category copy in `getPaymentErrorMessage`, so this only names the
 * codes for which something more useful than the category can be said. A
 * plain decline — `card_declined`, `do_not_honor`, `generic_decline` and the
 * rest of the bank's ways of saying no — is deliberately absent: the category
 * copy already says "try a different card", which is all there is to say.
 *
 * The decline copy is Stripe's own guidance to cardholders, reworded.
 */
const CONTACT_YOUR_BANK =
  "Your card was declined. Please contact your bank or try a different card.";

const STRIPE_COPY: Record<string, string> = {
  // ---- declines with something more to say ------------------------------
  insufficient_funds:
    "Your card has insufficient funds. Please try a different card.",
  withdrawal_count_limit_exceeded:
    "Your card has reached its spending limit. Please try a different card.",
  card_velocity_exceeded:
    "Your card has reached its spending limit. Please try a different card.",
  call_issuer: CONTACT_YOUR_BANK,
  issuer_not_available: CONTACT_YOUR_BANK,
  invalid_account: CONTACT_YOUR_BANK,
  new_account_information_available: CONTACT_YOUR_BANK,
  lost_card: CONTACT_YOUR_BANK,
  stolen_card: CONTACT_YOUR_BANK,
  pickup_card: CONTACT_YOUR_BANK,
  restricted_card: CONTACT_YOUR_BANK,
  security_violation: CONTACT_YOUR_BANK,
  fraudulent: CONTACT_YOUR_BANK,
  merchant_blacklist: CONTACT_YOUR_BANK,
  reenter_transaction: "Something went wrong with your card. Please try again.",
  try_again_later:
    "Your bank couldn't process this charge. Please try again in a moment.",
  duplicate_transaction:
    "It looks like this charge was already attempted. Please wait a moment before trying again.",
  authentication_required:
    "Your bank requires additional verification for this card. Please try a different card.",
  card_not_supported:
    "Your card doesn't support this type of purchase. Please try a different card.",
  currency_not_supported:
    "Your card doesn't support charges in US dollars. Please try a different card.",
  testmode_decline: "Test cards can't be used for real charges.",
  live_mode_test_card: "Test cards can't be used for real charges.",
  test_mode_live_card:
    "Real cards can't be used in test mode. Please use a Stripe test card.",

  // ---- card details -----------------------------------------------------
  incorrect_number: "Your card number is invalid. Please double check it.",
  invalid_number: "Your card number is invalid. Please double check it.",
  invalid_expiry_month: "Your card's expiration date is invalid.",
  invalid_expiry_year: "Your card's expiration date is invalid.",
  invalid_expiry_year_past: "Your card's expiration date is in the past.",
  invalid_cvc: "Your card's security code is invalid.",
  incorrect_cvc: "Your card's security code is incorrect.",
  incorrect_zip:
    "Your card's billing zip code doesn't match. Please double check it.",
  invalid_card_type:
    "Those card details don't look right. Please double check them.",
  processing_error:
    "An error occurred while processing your card. Please try again in a moment.",

  // ---- record state, seen by admins reversing a charge ------------------
  charge_already_refunded: "This charge has already been refunded.",
  charge_already_captured: "This charge has already been captured.",
  charge_disputed:
    "This charge is being disputed and can't be refunded right now.",
  payment_intent_unexpected_state:
    "This transaction can't be reversed in its current state.",
  amount_too_small: "That amount is below what the payment processor allows.",
  amount_too_large: "That amount is above what the payment processor allows.",
};

export const translateStripeCode = (code?: string): string | null =>
  (code && STRIPE_COPY[code]) || null;
